import { useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { subscribe, unsubscribe } from 'actions/subscriptions';
import { setTaskVisibility } from 'actions/tasks';

import { isPersonalTask } from 'utils/task';

import { PublicPageContext } from 'context/PublicPageContext';
import { RolesAndPermissionsContext } from 'context/RolesAndPermissionsContext';

import CanUser from 'components/CanUser';
import FeatureAvailable from 'components/feature-available';
import TimeTrackerCta from 'components/time-tracker-cta';
import Tooltip from 'components/tooltip';
import PlanUpgradeButton from 'components/PlanUpgradeButton';
import HideFrom from 'components/hide-from';
import TaskActions from 'components/TaskActions';

import IconBell from 'icons/icon-bell';
import IconEye from 'icons/icon-eye';

const ALLOWED_ACTIONS = ['addTimeEntry', 'viewTimeReport', 'taskLink', 'tieToGoal', 'changeMilestone', 'changeTaskGroup', 'duplicate', 'createTemplate', 'copyURL', 'archive', 'remove'];
const SMALL_SCREEN_ALLOWED_ACTIONS = ['changeMilestone', 'changeTaskGroup', 'duplicate', 'copyURL', 'archive', 'remove']

const TaskTopBarActions = ({ id, task, subscribed, isSmallScreen, onRemove }) => {
    const { t } = useTranslation(['task', 'billing']);
    const publicPageCtx = useContext(PublicPageContext);
    const rolesCtx = useContext(RolesAndPermissionsContext);
    const isPublic = publicPageCtx?.isPublic === true;
    const isPersonal = isPersonalTask(task);

    const handleSubscribeToggleClick = () => {
        if (subscribed) {
            unsubscribe('tasks', id);
        } else {
            subscribe('tasks', id);
        }
    }

    const handleTaskReveal = roleIds => {
        setTaskVisibility(id, true, roleIds);
    }

    const handleTaskHide = roleIds => {
        setTaskVisibility(id, false, roleIds);
    }

    return (
        <div className="content-panel-simple-actions">
            <CanUser type="startStopTimer">
                {/*<HarvestButton taskId={id} projectId={task.project} />*/}

                <FeatureAvailable type="timeTracking">
                    <div className="content-panel-simple-action">
                        <div className="content-panel-simple-action-inner">
                            <TimeTrackerCta key={id} taskId={id} projectId={task.project} iconOnly={true} />
                        </div>
                    </div>
                </FeatureAvailable>

                <FeatureAvailable type="timeTracking" reverse={true}>
                    <div className="content-panel-simple-action">
                        <Tooltip
                            className="light help-tooltip"
                            tooltip={
                                <Trans
                                    i18nKey="billing:upgrade.timeTracking"
                                    components={{
                                        title: <strong />,
                                        button: <PlanUpgradeButton reason="timeTracking" />,
                                        LearnMore: <a href="http://help.niftypm.com/en/articles/3059016-time-tracking-reporting" target="_blank" rel="noopener noreferrer" />
                                    }}
                                    values={{ plan: t('billing:plans.Starter.title') }}
                                />
                            }
                            placement="bottom-start"
                        >
                            <div className="content-panel-simple-action-inner">
                                <TimeTrackerCta placeholder={true} />
                            </div>
                        </Tooltip>
                    </div>
                </FeatureAvailable>
            </CanUser>

            {
                !isPersonal && !isPublic ? (
                    <Tooltip tooltip={subscribed ? <span>{ t('Unsubscribe description') }</span> : <span><Trans i18nKey="task:Subscribe description" /></span>}>
                        <div className={'content-panel-simple-action' + (subscribed ? ' active' : '')} onClick={handleSubscribeToggleClick}>
                            <div className="content-panel-simple-action-inner">
                                <IconBell />
                            </div>
                        </div>
                    </Tooltip>
                ) : null
            }

            {
                !isPersonal ? (
                    <CanUser type="hideTask">
                        <div className={'content-panel-simple-action' + (task.hidden?.length ? ' active' : '')}>
                            <HideFrom
                                onResourceHide={handleTaskHide}
                                onResourceReveal={handleTaskReveal}
                                hidden={task.hidden}
                                creator={task.created_by}
                            >
                                <div>
                                    <Tooltip tooltip={task.hidden?.length ? (
                                        <span>{ t('Task hidden from', { roles: rolesCtx.getRolesListString(task.hidden, true) }) }</span>
                                    ) : (
                                        <span>{ t('Hide task from') }</span>
                                    )}>
                                        <div className="content-panel-simple-action-inner">
                                            <IconEye size="16px" />
                                        </div>
                                    </Tooltip>
                                </div>
                            </HideFrom>
                        </div>
                    </CanUser>
                ) : null
            }

            <TaskActions
                id={id}
                className="content-panel-simple-action"
                innerClassName="content-panel-simple-action-inner"
                allowedActions={isSmallScreen ? SMALL_SCREEN_ALLOWED_ACTIONS : ALLOWED_ACTIONS}
                onRemove={onRemove}
            />
        </div>
    );
}

export default TaskTopBarActions;
